import { createClient } from '@supabase/supabase-js';

// IMPORTANT: Using the same Supabase project for both development and production
// This ensures schema consistency and simplifies testing
// If you need to separate data, consider adding an 'environment' column to your tables

// Get Supabase credentials from environment variables
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Log warning if environment variables are missing
if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Supabase credentials missing! Please check your .env file');
  console.error('Make sure you have REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY defined');
}

// Make sure URL has https:// prefix
let formattedUrl = supabaseUrl;
if (formattedUrl && !formattedUrl.startsWith('http')) {
  formattedUrl = `https://${formattedUrl}`;
}

// Connection health tracking
let connectionHealthy = true;
let lastConnectionCheck = 0;
const CONNECTION_CHECK_INTERVAL = 60000; // 1 minute

// Create the Supabase client
let supabase = null;
try {
  if (formattedUrl && supabaseAnonKey) {
    supabase = createClient(formattedUrl, supabaseAnonKey);
  } else {
    console.error('Cannot initialize Supabase client: missing URL or key');
    connectionHealthy = false;
  }
} catch (error) {
  console.error('Error creating Supabase client:', error);
  connectionHealthy = false;
}

// Health check function to prevent repeated failed attempts
const checkSupabaseHealth = async () => {
  const now = Date.now();
  
  // If we recently checked and it was unhealthy, don't check again
  if (!connectionHealthy && (now - lastConnectionCheck) < CONNECTION_CHECK_INTERVAL) {
    return false;
  }
  
  // If no supabase client, mark as unhealthy
  if (!supabase) {
    connectionHealthy = false;
    lastConnectionCheck = now;
    return false;
  }
  
  // Try a simple health check
  try {
    // Simple ping to check if Supabase is reachable
    const { error } = await supabase.from('saved_translations').select('id').limit(1);
    connectionHealthy = !error;
    lastConnectionCheck = now;
    
    if (error && !connectionHealthy) {
      console.warn('Supabase connection unhealthy, disabling database features temporarily');
    }
    
    return connectionHealthy;
  } catch (error) {
    connectionHealthy = false;
    lastConnectionCheck = now;
    console.warn('Supabase health check failed, working in offline mode');
    return false;
  }
};

export { supabase, checkSupabaseHealth };